import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { Quote, ChevronLeft, ChevronRight, Target, Sparkles, User } from 'lucide-react';
import { Button } from "../../components/ui/button";

const personas = [
  {
    quote: "I solve 50 problems a day, but my mock test scores are stuck at 140/300. I don't know if it's my concepts or just silly mistakes.",
    author: "The JEE Aspirant",
    role: "Targeting IIT Bombay",
    avatar: "JA",
    metric: "Needs Error Classification"
  },
  {
    quote: "My son studies hard, but he can't explain why he loses marks in Physics derivations. I can't help him because the syllabus has changed.",
    author: "The Concerned Parent",
    role: "Class 12 Parent",
    avatar: "CP",
    metric: "Needs Transparency"
  },
  {
    quote: "I'm consistently scoring 90%, but I can't find that hidden 10% gap. I need to know if I'm losing marks on technical keywords or step-wise presentation.",
    author: "The Perfectionist",
    role: "Class 10, ICSE Topper",
    avatar: "PT",
    metric: "Needs Step-Wise Audit"
  },
  {
    quote: "I know the concepts, but I panic during the exam and misread the questions. I need to practice specifically for exam temperament.",
    author: "The Anxious Student",
    role: "NEET Candidate",
    avatar: "AS",
    metric: "Needs Strategy Analysis"
  }
];

// Desktop Persona Card - CLEAN & STATIC
function PersonaCard({ persona, index }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: index * 0.1, duration: 0.5 }}
      data-testid={`persona-${index}`}
      className="bg-card rounded-2xl border border-border p-8 h-full flex flex-col"
    >
      <div className="relative flex-1 flex flex-col">
        {/* Header Badge - Static */}
        <div className="flex items-center gap-2 mb-6">
           <div className="p-1.5 rounded-lg bg-primary/10">
             <Target className="w-4 h-4 text-primary" />
           </div>
           <span className="text-xs font-bold text-primary tracking-wider uppercase">Target Profile</span>
        </div>

        {/* Quote Icon */}
        <div className="mb-4">
          <Quote className="w-8 h-8 text-muted-foreground/20" />
        </div>

        {/* The Struggle Text */}
        <p className="text-lg text-foreground/80 leading-relaxed mb-8 italic flex-1">
          "{persona.quote}"
        </p>

        {/* Solution Badge - Static */}
        <div className="inline-flex items-center px-3 py-1.5 rounded-lg bg-secondary/10 text-secondary text-sm font-medium mb-8 w-fit">
          <Sparkles className="w-3.5 h-3.5 mr-2" />
          {persona.metric}
        </div>

        {/* Profile Footer */}
        <div className="flex items-center gap-4 pt-6 border-t border-border mt-auto">
          <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center border border-border">
            <User className="w-6 h-6 text-muted-foreground" />
          </div>
          <div>
            <p className="font-semibold text-base">{persona.author}</p>
            <p className="text-sm text-muted-foreground">{persona.role}</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function PersonasSection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  useEffect(() => {
    if (!autoPlay) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % personas.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [autoPlay]);

  const nextPersona = () => {
    setAutoPlay(false);
    setCurrentIndex((prev) => (prev + 1) % personas.length);
  };

  const prevPersona = () => {
    setAutoPlay(false);
    setCurrentIndex((prev) => (prev - 1 + personas.length) % personas.length);
  };

  return (
    <section 
      ref={sectionRef}
      data-testid="personas-section"
      className="relative py-28 md:py-36 overflow-hidden bg-muted/30"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header - Static */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
            <Target className="w-5 h-5 text-primary" />
            <span className="text-lg font-medium text-primary">Who is this for?</span>
          </div>
          
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-8">
            Built for students who
            <br />
            <span className="text-primary">want to understand why.</span>
          </h2>
          
          <p className="text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto">
            Edalyze isn't just a grader. It's the diagnostic tool for ambitious learners.
          </p>
        </motion.div>

        {/* Desktop Grid */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {personas.map((persona, index) => (
            <PersonaCard key={index} persona={persona} index={index} />
          ))}
        </div>

        {/* Mobile Carousel */}
        <div className="md:hidden relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <PersonaCard persona={personas[currentIndex]} index={currentIndex} />
            </motion.div>
          </AnimatePresence>

          <div className="flex items-center justify-center gap-4 mt-8">
            <Button
              variant="outline"
              size="icon"
              onClick={prevPersona}
              className="rounded-full w-12 h-12"
            >
              <ChevronLeft className="w-6 h-6" />
            </Button>
            
            <div className="flex gap-2">
              {personas.map((_, index) => (
                <div 
                  key={index}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    index === currentIndex ? 'bg-primary' : 'bg-muted-foreground/30'
                  }`}
                />
              ))}
            </div>
            
            <Button
              variant="outline"
              size="icon"
              onClick={nextPersona}
              className="rounded-full w-12 h-12"
            >
              <ChevronRight className="w-6 h-6" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
